import React from "react";
import type { MyBadgeRankResponse } from "../types";
import { fmtInt, fmtPoints } from "../collection/badgeFormat";
import "./badgeShowcase.css";

/** The week-on-week move: positive is up (a smaller rank number). */
export function rankChange(rank: number | null, previousRank: number | null): number | null {
  if (rank === null || previousRank === null) return null;
  return previousRank - rank;
}

function LockIcon() {
  return (
    <svg className="bshow-rank__lock-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <rect x="2.5" y="7" width="11" height="8" fill="currentColor" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/**
 * The private badge rank. It shows the member's own numbers and nothing about
 * anyone else: the rank, how many are ranked, the top percentage, the gap to the
 * next place, and the change since last week.
 */
const BadgeRankCard: React.FC<{ rank: MyBadgeRankResponse | undefined; loading?: boolean }> = ({ rank, loading }) => {
  let body: React.ReactNode;
  if (!rank) {
    body = <p className="bshow-rank__sub">{loading ? "Working out your rank" : "Your rank is not available right now."}</p>;
  } else if (rank.rank === null) {
    body = (
      <>
        <div className="bshow-rank__big">No rank yet</div>
        <p className="bshow-rank__sub">Earn your first badge to get a place in the ranking.</p>
      </>
    );
  } else {
    const change = rankChange(rank.rank, rank.previousRank);
    body = (
      <>
        <div className="bshow-rank__big">
          #{fmtInt(rank.rank)} <span className="bshow-rank__of">of {fmtInt(rank.of)}</span>
        </div>
        <div className="bshow-rank__row">
          {/* "top 98%" reads like praise for second to last, so the chip only shows in the upper half. */}
          {rank.percentile !== null && rank.percentile <= 50 && (
            <span className="bshow-rank__chip">top {rank.percentile}%</span>
          )}
          {change !== null && (
            <span
              className={`bshow-rank__move ${change > 0 ? "is-up" : change < 0 ? "is-down" : "is-flat"}`}
              aria-label={
                change > 0
                  ? `up ${change} since last week`
                  : change < 0
                    ? `down ${-change} since last week`
                    : "no change since last week"
              }
            >
              <span aria-hidden="true">{change > 0 ? "▲" : change < 0 ? "▼" : "="}</span>
              {change !== 0 ? ` ${Math.abs(change)}` : ""} since last week
            </span>
          )}
        </div>
        <p className="bshow-rank__sub">
          {rank.rank === 1
            ? "You are at the top."
            : rank.pointsToNext !== null
              ? `${fmtPoints(rank.pointsToNext)} ${rank.pointsToNext === 1 ? "point" : "points"} to #${fmtInt(rank.rank - 1)}`
              : null}
        </p>
      </>
    );
  }

  return (
    <section className="bshow-rank" aria-labelledby="bshow-rank-title">
      <h3 id="bshow-rank-title" className="bshow-rank__title">
        Your badge rank
      </h3>
      {body}
      <p className="bshow-rank__lock">
        <LockIcon /> Only you can see this.
      </p>
    </section>
  );
};

export default BadgeRankCard;
